import { evaluateExpression } from '../../../../../../expressions/evaluate/evaluateExpression.ts';
import { type IEvaluationVariables } from '../../../../../../expressions/evaluate/variables/IEvaluationVariables.ts';
import { collectExpressionReferences } from '../../../../../../expressions/references/collectExpressionReferences.ts';
import { type IConditionGroupExpression } from '../../../../../../expressions/repository/condition-group/IConditionGroupExpression.ts';
import {
  type IWidgetOption,
  type IWidgetOptionValue,
} from '../../../../../Widget.ts';

// Tipi utilizzati per la gestione  delle utility

type OptionExpression = ReturnType<
  typeof OptionExpressionUtility.getPropertyExpression
>[0];

type ExpressionReferences = ReturnType<
  typeof OptionExpressionUtility.getExpressionsReferences
>[0];

/**
 * Utility per la gestione delle opzioni editabili/visibili (-> visibili in TODO).
 * ovver quelle selezionabili in una single/multi select
 * in base alle espressioni di editabilità.
 */
export class OptionExpressionUtility {
  /**
   * Ritorna vero se l'opzione ha un'espressione (enabled/visible) annessa
   */
  static hasExpression(
    property: 'enabled' | 'visible',
    option: IWidgetOption,
  ): option is IWidgetOptionValue & {
    enabled: { type: 'expression'; expression: IConditionGroupExpression };
  } {
    if (option.type !== 'option') return false;

    const optionProperty = option[property];
    if (!optionProperty) return false;

    return (
      option.type === 'option' &&
      optionProperty.type === 'expression' &&
      !!optionProperty.expression
    );
  }

  /** Ritorna falso se l'opzione non è modificabile/visibile (`enabled`) */
  static getPropertyResult(
    property: 'enabled' | 'visible',
    option: IWidgetOptionValue,
    variables: IEvaluationVariables,
  ) {
    const optionProperty = option[property];
    if (!optionProperty) return null;

    // Se è staticamente editabile, ritorno il valore statico
    if (optionProperty.type === 'static') {
      return optionProperty.value;
    }

    // Se è editabile tramite espressione, valuto l'espressione
    if (optionProperty.type === 'expression') {
      const expression = optionProperty.expression;
      const result = evaluateExpression(expression, { variables });
      return result;
    }

    if (process.env.NODE_ENV === 'development') {
      // biome-ignore format: <tbd>
      console.warn('[OptionExpressionUtility] L opzione non soddisfa nessun caso', { option });
    }
    return null;
  }

  /**
   * Ritorna le espressioni di enabled di ogni option,
   * collegandole al valore dell'opzione.
   * `{ [option-value]: expression }`
   */
  static getPropertyExpression(
    property: 'enabled' | 'visible',
    options: IWidgetOption[],
  ) {
    return options
      .map(o => {
        if (!OptionExpressionUtility.hasExpression(property, o)) return null;
        return { option: o.value, expression: o.enabled.expression };
      })
      .filter(
        (e): e is { option: any; expression: IConditionGroupExpression } =>
          e !== null,
      );
  }

  /**
   * Ritorna tutti i riferimenti per le espressioni contenute nelle opzioni
   */
  static getExpressionsReferences(expressions: OptionExpression[]) {
    return expressions.flatMap(e => collectExpressionReferences(e.expression));
  }
}
